/* global videojs */
const createCustomComponents = (bcInstance) => {
    const Component = videojs.getComponent('Component');
    const chaptersContainer = document.querySelector('.js_chaptersContainer');
    const playbackRateMenuButton = bcInstance._player.getDescendant(['ControlBar', 'PlaybackRateMenuButton']);
    const context = window.__VUMEDI_VIDEO_DETAIL_CONTEXT__;
    const currentPlaybackRate = bcInstance._loadUserPlaybackRate();

    let customControlsWrap;
    let playbackRateControlsWrap;
    let seekLabel;
    let playPauseButton;
    let seekForwardButton;
    let seekBackwardButton;
    let tapToUnmuteButton;

    const playPauseVideo = () => {
        if (bcInstance._player.paused()) {
            bcInstance._player.play();
        } else {
            bcInstance._player.pause();
        }
    };

    const setTapToUnmute = () => {
        if (!bcInstance._player.muted()) {
            return;
        }

        tapToUnmuteButton.classList.add('is-visible');
    };

    const trackTapToUnmute = (source) => {
        const eventTimestampMiliseconds = Math.floor(bcInstance._currentTime * 1000);
        window.eventTracker.trackTapToUnmute(source, eventTimestampMiliseconds);
        gtag('event', 'player_unmuted', {
            event_category: 'Video',
            event_label: context?.videoId,
            video_timestamp: eventTimestampMiliseconds,
        });
        window.vumedi.mixpanel?.track('Player Unmuted', {
            viewed_uuid: context.viewedUUID,
            video_timestamp: eventTimestampMiliseconds,
        });
    };

    /**
     * Handle 'Tap to unmute' player event by hiding the button and tracking an event.
     *
     * @param {'tap-to-unmute-button'|'volume-control'} source
     */
    const handleTapToUnmute = (source) => {
        tapToUnmuteButton.classList.remove('is-visible');
        bcInstance._player.muted(false);

        if (bcInstance._player.volume() === 0) {
            bcInstance._player.volume(1);
        }

        trackTapToUnmute(source);
    };

    const customControlsWrapClickHandler = (e) => {
        // if we click on the custom controls wrap element on desktop, we should toggle play/pause
        const clickTargetIsCorrect = e.target.classList.contains('js_videoInnerWrap');
        if (bcInstance._isTouchDevice || !clickTargetIsCorrect) return;
        playPauseVideo();
    };

    const customControlsWrapTouchEndHandler = (e) => {
        const clickTargetIsCorrect = e.target.classList.contains('js_videoInnerWrap');
        const controlsAreShown = customControlsWrap.classList.contains('show-seek-controls');

        if (bcInstance._isTouchDevice && clickTargetIsCorrect) {
            if (controlsAreShown) {
                customControlsWrap.classList.remove('show-seek-controls');
                customControlsWrap.classList.add('disable-hover-styles');

                setTimeout(() => {
                    bcInstance._player.userActive(false);
                }, 1);
            } else {
                customControlsWrap.classList.add('show-seek-controls');
                bcInstance._startControlsTimeout('touchend', customControlsWrap);
            }
        }
    };

    const customControlsWrapMouseMoveHandler = () => {
        if (bcInstance._isTouchDevice) return; // just quit if we're on a touch device
        customControlsWrap?.classList.remove('disable-hover-styles');
        bcInstance._startControlsTimeout('mouseover', customControlsWrap);
    };

    // hide controls when someone clicks outside of the video
    document.addEventListener('click', (e) => {
        const targetElement = e.target;
        if (!targetElement.closest('.js_videoInnerWrap')) {
            customControlsWrap?.classList.remove('show-seek-controls');
            bcInstance._player.userActive(false);
            seekLabel?.classList.remove('is-visible');
        }
    });

    class CustomSeekAndPlayControls extends Component {
        createEl() {
            customControlsWrap = videojs.dom.createEl('div', {
                className: 'vjs-custom-controls-wrap js_videoInnerWrap',
            });

            customControlsWrap.addEventListener('click', (e) => customControlsWrapClickHandler(e));
            customControlsWrap.addEventListener('touchend', (e) => customControlsWrapTouchEndHandler(e));
            customControlsWrap.addEventListener('mousemove', () => customControlsWrapMouseMoveHandler());

            seekLabel = videojs.dom.createEl('div', {
                className: 'vjs-seek-label js_videoSeekLabel',
                innerHTML: `
                    <i class="icon icon-seek"></i>
                    <p>10 seconds</p>
                `,
            });

            const customControls = videojs.dom.createEl('div', {
                className: 'vjs-custom-controls js_videoCustomControls',
            });

            // seek backward button
            seekBackwardButton = videojs.dom.createEl('button', {
                className: 'vjs-custom-control-button vjs-seek-backward-button js_videoSeekBackward',
                controlText: 'Seek backward button',
                innerHTML: '<i class="icon icon-seek-backward"></i>',
            });

            seekBackwardButton?.addEventListener('click', () => {
                bcInstance._seekVideo('backward', 'button');
                bcInstance._startControlsTimeout('click', customControlsWrap);
            });

            // play/pause button
            playPauseButton = videojs.dom.createEl('button', {
                className: 'vjs-custom-control-button vjs-play-pause-button js_videoPlayPause',
                controlText: 'Seek backward button',
                innerHTML: `
                    <i class="icon icon-play"></i>
                    <i class="icon icon-pause"></i>
                `,
            });

            playPauseButton.addEventListener('click', () => playPauseVideo());

            // seek forward button
            seekForwardButton = videojs.dom.createEl('button', {
                className: 'vjs-custom-control-button vjs-seek-forward-button js_videoSeekForward',
                controlText: 'Seek forward button',
                innerHTML: '<i class="icon icon-seek-forward"></i>',
            });

            seekForwardButton?.addEventListener('click', () => {
                bcInstance._seekVideo('forward', 'button');
                bcInstance._startControlsTimeout('click', customControlsWrap);
            });

            // Append the buttons to the wrapper div
            customControls.appendChild(seekBackwardButton);
            customControls.appendChild(playPauseButton);
            customControls.appendChild(seekForwardButton);
            customControlsWrap.appendChild(seekLabel);
            customControlsWrap.appendChild(customControls);

            return customControlsWrap;
        }
    }

    class CustomPlaybackRateControls extends Component {
        createEl() {
            const playbackRates = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

            const playbackRateControlsContainer = videojs.dom.createEl('div', {
                className: 'vjs-custom-playback-rate-container',
                innerHTML: '<div class="vjs-custom-playback-rate-screen-overlay"></div>',
            });

            playbackRateControlsWrap = videojs.dom.createEl('div', {
                className: 'vjs-custom-playback-rate-wrap js_playRateOptionsWrap',
            });

            const closeButton = videojs.dom.createEl('button', {
                className: 'vjs-playback-rate-close js_playRateCloseButton',
                controlText: 'Hide playback rate options',
                innerHTML: '<i class="icon icon-x" />',
            });

            closeButton.addEventListener('click', () => {
                playbackRateControlsWrap.classList.remove('is-expanded');
            });

            const controlsWrapHeader = videojs.dom.createEl('div', {
                className: 'vjs-playback-rate-header',
                innerHTML: '<p>Playback speed</p>',
            });

            const playbackRateOptions = videojs.dom.createEl('div', {
                className: 'vjs-playback-rate-options',
            });

            playbackRates.forEach((rate) => {
                const buttonClasses = `
                    vjs-playback-rate-option-button js_playRateOption ${rate === currentPlaybackRate ? 'is-active' : ''}
                `.trim();
                const playbackRateOptionButton = videojs.dom.createEl('button', {
                    className: buttonClasses,
                    controlText: `Set playback speed to ${rate}x`,
                    innerHTML: `${rate}x ${rate === 1 ? '(Normal)' : ''}`,
                });
                playbackRateOptionButton.dataset.playRate = rate;
                playbackRateOptionButton.addEventListener('click', () => {
                    const currentActiveItem = playbackRateControlsWrap?.querySelector('.js_playRateOption.is-active');
                    currentActiveItem?.classList.remove('is-active');
                    playbackRateOptionButton?.classList.add('is-active');

                    bcInstance._player.playbackRate(rate);
                    playbackRateControlsWrap.classList.remove('is-expanded');
                });
                playbackRateOptions.appendChild(playbackRateOptionButton);
            });

            controlsWrapHeader.appendChild(closeButton);
            playbackRateControlsWrap.appendChild(controlsWrapHeader);
            playbackRateControlsWrap.appendChild(playbackRateOptions);
            playbackRateControlsContainer.prepend(playbackRateControlsWrap);

            return playbackRateControlsContainer;
        }
    }

    class CustomVideoTimestampAndChapterTitle extends Component {
        createEl() {
            const metadataWrap = videojs.dom.createEl('div', {
                className: 'vjs-custom-metadata-container',
            });
            let chapterTitle;

            const controlBar = bcInstance._player.getChild('ControlBar');
            const currentTimeDisplay = bcInstance._player.getDescendant(['ControlBar', 'CurrentTimeDisplay']);
            const timeDivider = bcInstance._player.getDescendant(['ControlBar', 'TimeDivider']);
            const durationTimeDisplay = bcInstance._player.getDescendant(['ControlBar', 'DurationDisplay']);

            metadataWrap.appendChild(currentTimeDisplay.el_);
            metadataWrap.appendChild(timeDivider.el_);
            metadataWrap.appendChild(durationTimeDisplay.el_);

            if (chaptersContainer) {
                chapterTitle = videojs.dom.createEl('p', {
                    className: 'vjs-custom-metadata-chapter-title',
                    innerHTML: '<span></span>',
                });

                metadataWrap.appendChild(chapterTitle);
            }

            controlBar.el_.prepend(metadataWrap);
        }
    }

    class CustomTapToUnmuteButton extends Component {
        createEl() {
            tapToUnmuteButton = videojs.dom.createEl('button', {
                className: 'vjs-custom-control-button vjs-custom-control-button--tap-to-unmute js_tapToUnmute',
                innerHTML: `
                    <i class="icon icon-volume-mute2"></i>
                    Tap to unmute
                `,
            });

            tapToUnmuteButton.addEventListener('click', () => handleTapToUnmute('tap-to-unmute-button'));
            tapToUnmuteButton.addEventListener('touchend', () => handleTapToUnmute('tap-to-unmute-button'));

            customControlsWrap.appendChild(tapToUnmuteButton);
        }
    }

    videojs.registerComponent('CustomSeekAndPlayControls', CustomSeekAndPlayControls);
    videojs.registerComponent('CustomPlaybackRateControls', CustomPlaybackRateControls);
    videojs.registerComponent('CustomVideoTimestampAndChapterTitle', CustomVideoTimestampAndChapterTitle);
    videojs.registerComponent('CustomTapToUnmuteButton', CustomTapToUnmuteButton);

    bcInstance._player.on('play', () => {
        playPauseButton?.classList.remove('is-paused');
    });

    bcInstance._player.on('pause', () => {
        playPauseButton?.classList.add('is-paused');
    });

    bcInstance._player.on('volumechange', () => {
        bcInstance._handleVolumeChange();

        if (!bcInstance._player.muted() && tapToUnmuteButton.classList.contains('is-visible')) {
            handleTapToUnmute('volume-control');
        }
    });

    bcInstance._player.one('loadedmetadata', () => setTapToUnmute());

    playbackRateMenuButton?.on('touchend', (e) => {
        e.preventDefault();
        playbackRateControlsWrap.classList.toggle('is-expanded');
    });

    playbackRateMenuButton?.on('click', () => {
        playbackRateControlsWrap.classList.toggle('is-expanded');
    });

    bcInstance._player.addChild('CustomSeekAndPlayControls', {});
    bcInstance._player.addChild('CustomPlaybackRateControls', {});
    bcInstance._player.addChild('CustomVideoTimestampAndChapterTitle', {});
    bcInstance._player.addChild('CustomTapToUnmuteButton', {});
};

export default createCustomComponents;
